import React, { useEffect, useState, useRef } from "react";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import api from "../lib/api";
import { useLiveMetrics, useLiveAlerts } from "../hooks/useLiveMetrics";

// ── Types ─────────────────────────────────────────────────────────────────────
interface Meter { id: number; serial_number: string; unit_number: string; property_name: string; status: string; }
interface DailyUsage { date: string; units_consumed: string; peak_flow_rate: string; }

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n: number, d = 1) =>
  n.toLocaleString("en-KE", { minimumFractionDigits: d, maximumFractionDigits: d });

function shortTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-KE", { hour: "2-digit", minute: "2-digit" });
}
function shortDate(str: string) {
  return new Date(str).toLocaleDateString("en-KE", { day: "numeric", month: "short" });
}

// ── Live pulse dot ────────────────────────────────────────────────────────────
function LiveDot({ connected }: { connected: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
      <span className={`w-2 h-2 rounded-full ${connected ? "bg-emerald-400 animate-pulse" : "bg-gray-300"}`} />
      <span className={connected ? "text-emerald-600" : "text-gray-400"}>
        {connected ? "LIVE" : "Offline"}
      </span>
    </span>
  );
}

// ── Custom tooltip ────────────────────────────────────────────────────────────
function ChartTip({ active, payload, label, suffix = "" }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 border border-white/10 rounded-xl px-3 py-2.5 shadow-2xl text-xs">
      <p className="text-gray-400 mb-1.5 font-medium">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} className="flex items-center gap-2 text-white">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-gray-400">{p.name}:</span>
          <span className="font-bold">{fmt(Number(p.value), 3)}{suffix}</span>
        </p>
      ))}
    </div>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, icon, color, live }: {
  label: string; value: string; sub?: string;
  icon: React.ReactNode; color: string; live?: boolean;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4 hover:shadow-md transition-shadow">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-widest">{label}</p>
          {live && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
        </div>
        <p className="text-2xl font-bold text-gray-800 leading-tight mt-0.5">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ── Section header ────────────────────────────────────────────────────────────
function SectionHeader({ title, sub, right }: { title: string; sub?: string; right?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between mb-4">
      <div>
        <h2 className="text-[15px] font-bold text-gray-800">{title}</h2>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
      {right}
    </div>
  );
}

// ── Live meter selector ───────────────────────────────────────────────────────
function MeterSelector({ meters, selected, onChange }: {
  meters: Meter[]; selected: number | null; onChange: (id: number) => void;
}) {
  return (
    <select
      className="text-xs bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
      value={selected ?? ""}
      onChange={(e) => onChange(Number(e.target.value))}
    >
      <option value="" disabled>Select meter…</option>
      {meters.map((m) => (
        <option key={m.id} value={m.id}>
          {m.serial_number} · {m.unit_number}
        </option>
      ))}
    </select>
  );
}

// ── Alert badge ───────────────────────────────────────────────────────────────
const alertColors: Record<string, string> = {
  critical: "bg-red-50 border-red-200 text-red-700",
  warning:  "bg-amber-50 border-amber-200 text-amber-700",
  info:     "bg-blue-50 border-blue-200 text-blue-600",
};
const alertIcons: Record<string, string> = { critical: "🚨", warning: "⚠️", info: "ℹ️" };

// ─────────────────────────────────────────────────────────────────────────────
//  ANALYTICS PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function AnalyticsPage() {
  const [meters,      setMeters]      = useState<Meter[]>([]);
  const [selectedId,  setSelectedId]  = useState<number | null>(null);
  const [dailyUsage,  setDailyUsage]  = useState<DailyUsage[]>([]);
  const [loading,     setLoading]     = useState(true);

  // ── WebSocket streams ──────────────────────────────────────────────────────
  const { reading, history, connected } = useLiveMetrics(selectedId);
  const { alerts, connected: alertsConnected } = useLiveAlerts();

  // ── Fetch static data ──────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      api.get("/meters/"),
      api.get("/analytics/usage/"),
    ]).then(([mRes, uRes]) => {
      const mList: Meter[] = mRes.data.results ?? mRes.data;
      setMeters(mList);
      if (mList.length > 0) setSelectedId(mList[0].id);
      setDailyUsage(uRes.data.results ?? uRes.data ?? []);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // ── Derived stats ──────────────────────────────────────────────────────────
  const currentFlow    = reading ? parseFloat(reading.flow_rate)  : 0;
  const currentPressure= reading ? parseFloat(reading.pressure)   : 0;
  const currentCum     = reading ? parseFloat(reading.cumulative) : 0;

  // Total usage from today's history window
  const historyConsumption = history.length >= 2
    ? history[history.length - 1].cumulative - history[0].cumulative
    : 0;

  // Peak flow in this session
  const peakFlow = history.reduce((m, p) => Math.max(m, p.flow_rate), 0);

  // Daily chart data
  const dailyChartData = dailyUsage.slice(-30).map((d) => ({
    date:    shortDate(d.date),
    usage:   parseFloat(d.units_consumed),
    peak:    parseFloat(d.peak_flow_rate),
  }));

  // Hourly distribution from history (bucket by hour)
  const hourlyMap: Record<number, number> = {};
  history.forEach((p) => {
    const h = new Date(p.timestamp).getHours();
    hourlyMap[h] = (hourlyMap[h] || 0) + p.flow_rate;
  });
  const hourlyData = Array.from({ length: 24 }, (_, h) => ({
    hour:  `${String(h).padStart(2, "0")}:00`,
    flow:  hourlyMap[h] ?? 0,
  }));

  // Valve open vs closed time from history
  const valveOpenCount  = history.filter((_, i) =>
    reading?.valve_open).length;
  const valveClosedCount = history.length - valveOpenCount;

  const selectedMeter = meters.find((m) => m.id === selectedId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-5 space-y-6 max-w-[1400px] mx-auto">

      {/* ── Page header ─────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Live Analytics</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            Real-time water usage · token consumption · pressure monitoring
          </p>
        </div>
        <div className="flex items-center gap-3">
          <LiveDot connected={connected} />
          <MeterSelector meters={meters} selected={selectedId} onChange={setSelectedId} />
        </div>
      </div>

      {/* ── Live stat strip ──────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard
          label="Flow Rate" live
          value={`${fmt(currentFlow, 3)} L/h`}
          sub={`Peak: ${fmt(peakFlow, 3)} L/h`}
          color="bg-blue-50"
          icon={<svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6 8 4 12 4 15a8 8 0 0016 0c0-3-2-7-8-13z"/></svg>}
        />
        <StatCard
          label="Pressure" live
          value={`${fmt(currentPressure, 2)} bar`}
          sub={currentPressure >= 8 ? "⚠️ HIGH" : currentPressure <= 0.5 ? "⚠️ LOW" : "Normal"}
          color="bg-violet-50"
          icon={<svg className="w-5 h-5 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>}
        />
        <StatCard
          label="Cumulative" live
          value={`${fmt(currentCum, 2)} L`}
          sub={`+${fmt(historyConsumption, 3)} L this session`}
          color="bg-emerald-50"
          icon={<svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/></svg>}
        />
        <StatCard
          label="Valve"
          value={reading ? (reading.valve_open ? "Open" : "Closed") : "—"}
          sub={selectedMeter ? `${selectedMeter.serial_number}` : "No meter selected"}
          color={reading?.valve_open ? "bg-emerald-50" : "bg-red-50"}
          icon={<svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10"/><path strokeLinecap="round" d="M12 8v4l3 3"/></svg>}
        />
      </div>

      {/* ── Main charts row ───────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Flow rate — live scrolling line */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <SectionHeader
            title="Live Flow Rate"
            sub={`${history.length} data points · updates every 30 s`}
            right={<LiveDot connected={connected} />}
          />
          {history.length === 0 ? (
            <EmptyChart message="Waiting for live data…" />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={history}>
                <defs>
                  <linearGradient id="flowGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="time" tick={{ fontSize: 10, fill: "#94a3b8" }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} unit=" L/h" width={60} />
                <Tooltip content={<ChartTip suffix=" L/h" />} />
                <Line
                  type="monotone" dataKey="flow_rate" name="Flow"
                  stroke="#3b82f6" strokeWidth={2} dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Pressure — live area */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <SectionHeader
            title="Live Pressure"
            sub="Line pressure in bar · danger thresholds at 0.5 and 8.0 bar"
            right={<LiveDot connected={connected} />}
          />
          {history.length === 0 ? (
            <EmptyChart message="Waiting for live data…" />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={history}>
                <defs>
                  <linearGradient id="pressGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#8b5cf6" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="time" tick={{ fontSize: 10, fill: "#94a3b8" }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} unit=" bar" domain={[0, 10]} width={52} />
                <Tooltip content={<ChartTip suffix=" bar" />} />
                {/* Danger threshold lines */}
                <Area
                  type="monotone" dataKey="pressure" name="Pressure"
                  stroke="#8b5cf6" strokeWidth={2} fill="url(#pressGrad)"
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
          {/* Threshold legend */}
          <div className="flex gap-4 mt-2">
            <span className="text-[10px] text-red-400 font-medium flex items-center gap-1">
              <span className="w-4 border-t-2 border-dashed border-red-400" /> ≥ 8 bar: SPIKE
            </span>
            <span className="text-[10px] text-amber-400 font-medium flex items-center gap-1">
              <span className="w-4 border-t-2 border-dashed border-amber-400" /> ≤ 0.5 bar: DROP
            </span>
          </div>
        </div>
      </div>

      {/* ── Cumulative & token consumption ───────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <SectionHeader
          title="Cumulative Water Consumption — Live Session"
          sub="Total volume passed through the meter since session start"
          right={<LiveDot connected={connected} />}
        />
        {history.length === 0 ? (
          <EmptyChart message="Waiting for live data…" />
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={history}>
              <defs>
                <linearGradient id="cumGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="time" tick={{ fontSize: 10, fill: "#94a3b8" }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} unit=" L" width={60} />
              <Tooltip content={<ChartTip suffix=" L" />} />
              <Area
                type="monotone" dataKey="cumulative" name="Cumulative"
                stroke="#10b981" strokeWidth={2.5} fill="url(#cumGrad)"
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── 30-day historical usage + hourly distribution ─────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* 30-day daily usage bar */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <SectionHeader
            title="Daily Usage — Last 30 Days"
            sub="Total litres consumed per day across all meters"
          />
          {dailyChartData.length === 0 ? (
            <EmptyChart message="No historical data yet" />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={dailyChartData} barSize={8}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={4} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} unit=" L" width={52} />
                <Tooltip content={<ChartTip suffix=" L" />} />
                <Bar dataKey="usage" name="Usage" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Hourly flow distribution from live history */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <SectionHeader
            title="Hourly Flow Distribution"
            sub="Flow rate by hour of day (live session data)"
            right={<LiveDot connected={connected} />}
          />
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={hourlyData} barSize={8}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="hour" tick={{ fontSize: 9, fill: "#94a3b8" }} interval={3} />
              <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} unit=" L/h" width={56} />
              <Tooltip content={<ChartTip suffix=" L/h" />} />
              <Bar dataKey="flow" name="Flow" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Live alerts feed ─────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <SectionHeader
          title="Live System Alerts"
          sub="Leakage, pressure anomalies, and valve events"
          right={<LiveDot connected={alertsConnected} />}
        />
        {alerts.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">
            ✅ No alerts — all meters normal
          </p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {alerts.map((a) => (
              <div
                key={a.id}
                className={`flex items-start gap-3 rounded-xl border px-3.5 py-2.5 text-sm ${alertColors[a.level] || alertColors.info}`}
              >
                <span className="text-base mt-0.5">{alertIcons[a.level]}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium leading-snug">{a.message}</p>
                  {a.serial && <p className="text-[11px] opacity-70 mt-0.5">Meter: {a.serial}</p>}
                </div>
                <span className="text-[11px] opacity-60 shrink-0 mt-0.5">
                  {shortTime(a.timestamp)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

function EmptyChart({ message }: { message: string }) {
  return (
    <div className="h-[220px] flex flex-col items-center justify-center gap-2 text-gray-300">
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
      </svg>
      <p className="text-sm">{message}</p>
    </div>
  );
}
